<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="validateStatus === 'loading'"
      class="flex min-h-[280px] items-center justify-center">
      <div class="flex items-center gap-2 text-contrast-muted">
        <Loader :size="18" class="animate-spin" />
        <span>{{ i18n.t('loading') }}</span>
      </div>
    </div>
    <div v-else class="grid gap-6 md:grid-cols-2">
      <SmDialogMessage
        v-if="validateStatus === 'error' && props.validateErrorMessage"
        class="md:col-span-2"
        type="error"
        :visible="true"
        :message="props.validateErrorMessage" />
      <div class="flex flex-col gap-3">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div class="md:col-span-2">
            <h4 class="text-sm font-semibold text-contrast">
              {{ i18n.t('columnNames') }}
            </h4>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm text-contrast">
              {{ i18n.t('accountingimportcolumn.description') }}
            </label>
            <SmInput
              :model-value="configDraft.description_column ?? ''"
              class="w-full"
              design="form"
              :placeholder="autodetectLabelFormatted"
              :disabled="processingDisabled"
              @update:modelValue="
                emit('setColumnField', 'description_column', $event)
              " />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm text-contrast">
              {{ i18n.t('accountingimportcolumn.cost') }}
            </label>
            <SmInput
              :model-value="configDraft.cost_column ?? ''"
              class="w-full"
              design="form"
              :placeholder="autodetectLabelFormatted"
              :disabled="processingDisabled"
              @update:modelValue="
                emit('setColumnField', 'cost_column', $event)
              " />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm text-contrast">
              {{ i18n.t('accountingimportcolumn.currency') }}
            </label>
            <SmInput
              :model-value="configDraft.currency_column ?? ''"
              class="w-full"
              design="form"
              :placeholder="autodetectLabelFormatted"
              :disabled="processingDisabled"
              @update:modelValue="
                emit('setColumnField', 'currency_column', $event)
              " />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm text-contrast">
              {{ i18n.t('accountingimportcolumn.bookingDate') }}
            </label>
            <SmInput
              :model-value="configDraft.booking_date_column ?? ''"
              class="w-full"
              design="form"
              :placeholder="autodetectLabelFormatted"
              :disabled="processingDisabled"
              @update:modelValue="
                emit('setColumnField', 'booking_date_column', $event)
              " />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm text-contrast">
              {{ i18n.t('skipRows') }}
            </label>
            <SmInput
              :model-value="
                configDraft.skip_rows !== undefined &&
                configDraft.skip_rows !== null
                  ? String(configDraft.skip_rows)
                  : ''
              "
              type="number"
              class="w-full"
              design="form"
              :placeholder="autodetectLabelFormatted"
              :disabled="processingDisabled"
              @update:modelValue="
                emit('setNumberField', 'skip_rows', $event)
              " />
          </div>

          <div class="pt-6 md:col-span-2">
            <h4 class="text-sm font-semibold text-contrast">
              {{ i18n.t('costConfiguration') }}
            </h4>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm text-contrast">
              {{ i18n.t('costDecimalSeparator') }}
            </label>
            <SmInput
              :model-value="configDraft.cost_decimal_separator ?? ''"
              class="w-full"
              design="form"
              :placeholder="autodetectLabel"
              :disabled="processingDisabled"
              @update:modelValue="emit('setDecimalSeparator', $event)" />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm text-contrast">
              {{ i18n.t('currencyLabel') }}
            </label>
            <SmSelect
              v-model:selection="currencySelectionModel"
              class="currency-select w-full"
              :options="currencyOptions"
              :placeholder="autodetectLabelFormatted"
              :disabled="processingDisabled"
              trigger-style="default" />
          </div>

          <div class="flex items-center gap-2 pt-2 md:col-span-2">
            <SmSwitch
              :model-value="configDraft.enable_ai_prediction ?? false"
              :disabled="processingDisabled"
              @update:modelValue="emit('toggleAi', $event)" />
            <span class="text-sm font-medium text-contrast">
              {{ i18n.t('enableAiPrediction') }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex min-h-0 flex-col gap-3">
        <h3 class="text-base font-semibold text-contrast">
          {{ i18n.t('accountingImportSample') }}
        </h3>
        <div class="table-wrapper flex-1 overflow-auto rounded">
          <table class="preview-table w-full text-sm">
            <thead>
              <tr class="text-left text-contrast-muted">
                <th class="px-2 py-1">#</th>
                <th class="px-2 py-1">
                  {{ i18n.t('accountingimportcolumn.description') }}
                </th>
                <th class="px-2 py-1">
                  {{ i18n.t('accountingimportcolumn.cost') }}
                </th>
                <th class="px-2 py-1">
                  {{ i18n.t('accountingimportcolumn.bookingDate') }}
                </th>
                <th class="px-2 py-1">
                  {{ i18n.t('accountingimportcolumn.currency') }}
                </th>
                <th class="px-2 py-1">
                  {{ i18n.t('accountingimportcolumn.software') }}
                </th>
                <th class="px-2 py-1">
                  {{ i18n.t('accountingimportcolumn.blacklisted') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sample in validationSamples" :key="sample.row_index">
                <td class="px-2 py-1 text-contrast-muted">
                  <TextMultiline>{{ sample.row_index }}</TextMultiline>
                </td>
                <td class="px-2 py-1">
                  <TextMultiline>{{
                    sample.row_content?.description
                  }}</TextMultiline>
                </td>
                <td class="px-2 py-1">
                  <TextMultiline>{{ sample.row_content?.cost }}</TextMultiline>
                </td>
                <td class="px-2 py-1">
                  <TextMultiline>{{
                    sample.row_content?.booking_date
                  }}</TextMultiline>
                </td>
                <td class="px-2 py-1">
                  <TextMultiline>{{
                    sample.row_content?.currency
                  }}</TextMultiline>
                </td>
                <td class="px-2 py-1">
                  <TextMultiline>{{
                    sample.software_names?.join(', ')
                  }}</TextMultiline>
                </td>
                <td class="px-2 py-1">
                  <TextMultiline>
                    {{ sample.blacklisted ? i18n.t('yes') : i18n.t('no') }}
                  </TextMultiline>
                </td>
              </tr>
              <tr v-if="!validationSamples.length">
                <td
                  colspan="7"
                  class="px-2 py-3 text-center text-contrast-muted">
                  <TextMultiline>{{ i18n.t('noData') }}</TextMultiline>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Loader } from 'lucide-vue-next'

  import {
    AccountingImportConfigUpdate,
    AccountingImportRowSample,
  } from '@/client'
  import SmInput from '@/components/sm/SmInput.vue'
  import SmSelect, { Option } from '@/components/sm/SmSelect.vue'
  import SmSwitch from '@/components/sm/SmSwitch.vue'
  import { capitalize } from '@/utils/formatter/formatter'
  import SmDialogMessage from '@/components/sm/SmDialogMessage.vue'

  const i18n = useI18n()

  const props = defineProps<{
    configDraft: AccountingImportConfigUpdate
    currencyOptions: Option<string>[]
    currencySelection?: Option<string>[] | undefined
    autodetectLabel: string
    processingStatus: 'idle' | 'loading' | 'error'
    validateStatus: 'idle' | 'loading' | 'success' | 'error'
    validateErrorMessage?: string | null
    validationSamples: AccountingImportRowSample[]
  }>()

  const emit = defineEmits<{
    (e: 'update:currencySelection', value: Option<string>[] | undefined): void
    (
      e: 'setColumnField',
      key:
        | 'description_column'
        | 'cost_column'
        | 'currency_column'
        | 'booking_date_column',
      value: string
    ): void
    (
      e: 'setNumberField',
      key: 'skip_rows' | 'prediction_threshold',
      value: number | string
    ): void
    (e: 'setDecimalSeparator', value: string): void
    (e: 'toggleAi', value: boolean): void
  }>()

  const processingDisabled = computed(
    () => props.processingStatus === 'loading'
  )

  const autodetectLabelFormatted = computed(() =>
    props.autodetectLabel ? capitalize(props.autodetectLabel) : ''
  )

  const currencySelectionModel = computed({
    get() {
      return props.currencySelection ?? []
    },
    set(value: Option<string>[] | undefined) {
      emit('update:currencySelection', value)
    },
  })
</script>

<style lang="scss" scoped>
  :deep(.currency-select) {
    flex-grow: 1;
    > div {
      height: 100%;
    }
    .trigger {
      height: 100%;
    }
  }

  .dark {
    .table-wrapper {
      border: 1px solid theme('colors.gray[600]');
    }
    .preview-table {
      td:not(:last-of-type),
      thead th {
        border-right: 1px solid theme('colors.gray[600]');
      }

      tr:not(:last-child),
      thead tr {
        border-bottom: 1px solid theme('colors.gray[600]');
      }
    }
  }

  .table-wrapper {
    border: 1px solid theme('colors.gray[200]');
    max-height: 400px;
    overflow: scroll;
  }
  .grid {
    min-height: 0;
  }
  .preview-table {
    td:not(:last-of-type),
    thead th {
      border-right: 1px solid theme('colors.gray[200]');
    }

    tr:not(:last-child),
    thead tr {
      border-bottom: 1px solid theme('colors.gray[200]');
    }

    th,
    td {
      padding: 1rem;

      div {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
        /* optional: helps avoid breaking long words */
        word-break: break-word;
      }
    }
  }
</style>
